/**
 * Auth API: SMS yuborish, kod tekshirish, profil, chiqish.
 * Token localStorage da auth_token kaliti bilan saqlanadi.
 * sendSms — axios orqali, mutlaq backend URL-ga so'rov yuboradi.
 */
import axios from 'axios';
import api from './api';
import { buildAbsoluteUrl } from '../config/apiConfig';
import { endpoints } from './endpoints';
import { normalizeLoginPhone } from '../utils/uzPhone';

export { normalizeLoginPhone } from '../utils/uzPhone';

const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const LEGACY_TOKEN_KEY = 'auth_token';
const SESSION_ID_KEY = 'auth_session_id';

function getDeviceHeaders() {
  if (typeof window === 'undefined') return {};
  const ua = window.navigator?.userAgent ?? '';
  const isMobile = /Android|iPhone|iPad|Mobile/i.test(ua);
  return {
    'X-Device-Type': isMobile ? 'mobile' : 'web',
    'X-Device-Name': isMobile ? 'ADDE Web Mobile' : 'ADDE Web Desktop',
  };
}

/**
 * Kirish: +998901234567 yoki 901234567 (9 ta mahalliy raqam) → +998901234567
 * @see ../utils/uzPhone.js
 */

export function getStoredToken() {
  try {
    if (typeof window === 'undefined') return null;
    return (
      window.localStorage?.getItem?.(ACCESS_TOKEN_KEY) ??
      window.localStorage?.getItem?.(LEGACY_TOKEN_KEY) ??
      null
    );
  } catch {
    return null;
  }
}

export function getStoredRefreshToken() {
  try {
    return typeof window !== 'undefined' ? window.localStorage?.getItem?.(REFRESH_TOKEN_KEY) : null;
  } catch {
    return null;
  }
}

export function setStoredToken(token) {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      if (token) {
        window.localStorage.setItem(ACCESS_TOKEN_KEY, token);
        window.localStorage.removeItem(LEGACY_TOKEN_KEY);
      } else {
        window.localStorage.removeItem(ACCESS_TOKEN_KEY);
      }
    }
  } catch {}
}

export function setStoredRefreshToken(token) {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      if (token) window.localStorage.setItem(REFRESH_TOKEN_KEY, token);
      else window.localStorage.removeItem(REFRESH_TOKEN_KEY);
    }
  } catch {}
}

export function getStoredSessionId() {
  try {
    return typeof window !== 'undefined' ? window.localStorage?.getItem?.(SESSION_ID_KEY) : null;
  } catch {
    return null;
  }
}

export function setStoredSessionId(sessionId) {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      if (sessionId) window.localStorage.setItem(SESSION_ID_KEY, String(sessionId));
      else window.localStorage.removeItem(SESSION_ID_KEY);
    }
  } catch {}
}

export function setStoredTokens({ accessToken, refreshToken, sessionId = null }) {
  setStoredToken(accessToken);
  setStoredRefreshToken(refreshToken);
  setStoredSessionId(sessionId);
}

export function clearStoredToken() {
  setStoredToken(null);
  setStoredRefreshToken(null);
  setStoredSessionId(null);
}

/** AI Maslahat session UUID — `AdminMaslahatPage` bilan bir xil `sessionStorage` kaliti */
const ADVISOR_SESSION_STORAGE_KEY = 'neduz_maslahat_advisor_session';

/**
 * Maslahat sahifasida yaratilgan session id (sessionStorage).
 * Echo / real-time uchun authContext dan chaqiriladi.
 * @returns {string|null}
 */
export function getStoredAdvisorSessionId() {
  try {
    if (typeof window === 'undefined') return null;
    const ex = window.sessionStorage?.getItem?.(ADVISOR_SESSION_STORAGE_KEY);
    if (!ex || !String(ex).trim()) return null;
    return String(ex).trim();
  } catch {
    return null;
  }
}

/**
 * Telefon raqamiga SMS yuborish (axios).
 * `purpose` faqat aniq berilganda JSONga qo'shiladi — panel kirishda yubormang (oldingi backend shartnomasi).
 * Marketing: `sendMarketingOrderSms(phone)` yoki `sendSms(phone, 'marketing_order')`.
 * @param {string} phone - masalan "+998901234567"
 * @param {string} [purpose] - masalan "marketing_order"
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function sendSms(phone, purpose) {
  const url = buildAbsoluteUrl(endpoints.auth.sendSms);
  const body =
    purpose != null && String(purpose).trim() !== ''
      ? { phone, purpose: String(purpose).trim() }
      : { phone };
  return axios.post(url, body, {
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    timeout: 45000,
  });
}

/** Admin/panel — rolga kirish SMS; body { phone }; keyin POST /api/auth/verify-login. */
export async function sendLoginSms(phone) {
  return sendSms(phone);
}

/** Marketing do'kon checkout — alohida OTP; keyin verify-sms va verify_token bilan order. */
export async function sendMarketingOrderSms(phone) {
  return sendSms(phone, 'marketing_order');
}

/**
 * Marketing buyurtma uchun SMS kodni tekshiradi.
 * @param {string} phone
 * @param {string} code
 * @param {string} verificationId
 * @param {string} purpose
 */
export async function verifySms(phone, code, verificationId, purpose = 'marketing_order') {
  const url = buildAbsoluteUrl(endpoints.auth.verifySms);
  return axios.post(
    url,
    {
      phone,
      code,
      verification_id: verificationId,
      purpose,
    },
    {
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      timeout: 45000,
    },
  );
}

/**
 * SMS kod + telefon orqali kirish. Token va user qaytariladi.
 * Backend { data: { token, user } } yoki { token, user } formatida qaytarishi mumkin.
 * @param {string} phone
 * @param {string} code - 4 xonali kod
 * @returns {Promise<{ token: string, user: object }>}
 */
/**
 * Panel kirish: POST /api/auth/login-password
 * Body: { phone, password } — JSON, Content-Type: application/json (FormData emas).
 * 422: data.meta.auth_reason — invalid_credentials | phone_invalid | missing_or_invalid_fields
 */
export async function loginWithPassword(phone, password) {
  const normalizedPhone = normalizeLoginPhone(phone);
  const pwd = password != null ? String(password).trim() : '';
  if (!normalizedPhone || pwd.length < 1) {
    const err = new Error('missing_or_invalid_fields');
    err.code = 'missing_or_invalid_fields';
    err.response = { status: 422, data: { meta: { auth_reason: 'missing_or_invalid_fields' } } };
    throw err;
  }

  const url = buildAbsoluteUrl(endpoints.auth.loginPassword);
  const body = { phone: normalizedPhone, password: pwd };

  try {
    const res = await axios.post(url, body, {
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...getDeviceHeaders() },
      timeout: 20000,
    });

    const root = res?.data ?? {};
    const payload =
      root?.data && typeof root.data === 'object' && (root.data.access_token != null || root.data.user != null)
        ? root.data
        : root;

    const token =
      payload?.access_token ?? payload?.accessToken ?? payload?.token ?? root?.access_token ?? root?.accessToken ?? null;
    const refreshToken = payload?.refresh_token ?? payload?.refreshToken ?? root?.refresh_token ?? root?.refreshToken ?? null;
    const sessionId = payload?.session_id ?? payload?.sessionId ?? root?.session_id ?? root?.sessionId ?? null;
    const user = payload?.user ?? root?.user ?? null;

    return {
      token: token ? String(token).trim() : null,
      accessToken: token ? String(token).trim() : null,
      refreshToken: refreshToken ? String(refreshToken).trim() : null,
      sessionId: sessionId ? String(sessionId).trim() : null,
      user,
    };
  } catch (e) {
    const status = e?.response?.status;
    const data = e?.response?.data;
    const meta = data?.meta && typeof data.meta === 'object' ? data.meta : {};
    const authReason = meta.auth_reason ?? meta.authReason ?? null;
    const errCode = data?.code ?? null;
    if (status === 422 && (errCode === 'REQUIRES_OTP' || authReason === 'requires_otp')) {
      return { requiresOtp: true };
    }
    if (status === 422 && authReason) {
      const err = new Error(String(authReason));
      err.code = authReason;
      err.response = e.response;
      throw err;
    }
    throw e;
  }
}

export async function verifyLogin(phone, code) {
  const res = await api.post(endpoints.auth.verifyLogin, { phone, code });
  const data = res?.data ?? res;
  const token = data?.access_token ?? data?.accessToken ?? data?.token ?? res?.token ?? null;
  const refreshToken = data?.refresh_token ?? data?.refreshToken ?? null;
  const sessionId = data?.session_id ?? data?.sessionId ?? null;
  const user = data?.user ?? res?.user ?? null;
  return {
    token: token ? String(token).trim() : null,
    accessToken: token ? String(token).trim() : null,
    refreshToken: refreshToken ? String(refreshToken).trim() : null,
    sessionId: sessionId ? String(sessionId).trim() : null,
    user,
  };
}

/**
 * Joriy foydalanuvchi (Bearer token kerak)
 */
export async function getProfile() {
  const res = await api.get(endpoints.user);
  return res?.data ?? res;
}

export async function updateProfilePhone(phone) {
  const res = await api.put(endpoints.userUpdatePhone, { phone });
  return res?.data ?? res;
}

export async function updateProfileName(name) {
  const trimmed = name != null ? String(name).trim() : '';
  const res = await api.put(endpoints.user, { name: trimmed });
  return res?.data ?? res;
}

export async function updateRoleSwitchPin(params) {
  const body = {
    new_pin: params.newPin,
    new_pin_confirm: params.newPinConfirm,
  };
  const currentPin = params?.currentPin != null ? String(params.currentPin).trim() : '';
  if (currentPin) body.current_pin = currentPin;
  const res = await api.put(endpoints.userRoleSwitchPin, body);
  return res?.data ?? res;
}

export async function updateProfilePassword(params) {
  const currentPassword = params?.currentPassword != null ? String(params.currentPassword).trim() : '';
  const newPassword = params?.newPassword != null ? String(params.newPassword).trim() : '';
  const newPasswordConfirm = params?.newPasswordConfirm != null ? String(params.newPasswordConfirm).trim() : '';
  const body = {
    new_password: newPassword,
    new_password_confirm: newPasswordConfirm,
  };
  if (currentPassword) body.current_password = currentPassword;

  try {
    const res = await api.put(endpoints.userChangePassword, body);
    return res?.data ?? res;
  } catch (e) {
    // Backward compatibility: ba'zi backendlarda endpoint boshqa bo'lishi mumkin.
    if (e?.status === 404) {
      const fallback = await api.post('/api/auth/change-password', body);
      return fallback?.data ?? fallback;
    }
    throw e;
  }
}

export async function validateRoleSwitchPinApi(userId, pin) {
  const res = await api.post(endpoints.auth.validateRoleSwitchPin, { user_id: userId, pin });
  const data = res?.data ?? res;
  return { valid: !!data?.valid };
}

export async function impersonateCashier(cashierId, pin) {
  const payload = { cashier_id: cashierId };
  if (pin) (payload).pin = pin;
  const res = await api.post(endpoints.auth.impersonateCashier, payload);
  return res?.data ?? res;
}

/**
 * Kassir → Mijoz (tenant egasi) rolga o'tish.
 * Backend cashierning JWT'idan tenant egasini topib, PIN tekshiradi va yangi tokenlar qaytaradi.
 * @param {string} pin - Mijoz (tenant egasi)ning role_switch_pin'i
 */
export async function switchToOwner(pin) {
  const res = await api.post(endpoints.auth.switchToOwner, { pin });
  return res?.data ?? res;
}

/**
 * Kassir → boshqa Kassir rolga o'tish.
 * Maqsad cashierning PIN'ini tekshirib yangi tokenlar qaytaradi.
 * @param {string} cashierId - Maqsad cashier ID'si
 * @param {string} pin - Maqsad cashierning role_switch_pin'i
 */
export async function switchToCashier(cashierId, pin) {
  const res = await api.post(endpoints.auth.switchToCashier, { cashier_id: cashierId, pin });
  return res?.data ?? res;
}

/**
 * Pastdagi rollardan foydalanuvchilar ro'yxati (EGA, bosh menejer, viloyat menejer).
 * @param {{ q?: string; limit?: number }} params
 */
export async function fetchImpersonationTargets(params = {}) {
  const res = await api.get(endpoints.auth.impersonationTargets, {
    params: { q: params.q ?? '', limit: params.limit ?? 30 },
  });
  // Laravel: { data: [...] } — api.get() allaqachon JSON ildizini qaytaradi
  const rows = res?.data;
  return Array.isArray(rows) ? rows : [];
}

/**
 * Rollar bo'yicha guruhlangan ro'yxat (sidebar): grouped=1.
 * @param {{ q?: string; limit?: number }} params
 * @returns {Promise<Record<string, unknown[]>>}
 */
export async function fetchImpersonationTargetsGrouped(params = {}) {
  const res = await api.get(endpoints.auth.impersonationTargets, {
    params: {
      grouped: 1,
      q: params.q ?? '',
      limit: params.limit ?? 400,
    },
  });
  // Laravel: { grouped: {...}, data: [] } — data bo'sh [] bo'lsa ham grouped to'g'ri (avval res.data emas!)
  let g = res?.grouped;
  if (Array.isArray(g)) {
    g = null;
  }
  if (g && typeof g === 'object' && !Array.isArray(g)) {
    return g;
  }
  const flat = Array.isArray(res?.data) ? res.data : [];
  if (flat.length === 0) {
    return {};
  }
  const out = {};
  for (const row of flat) {
    const role = row?.role != null ? String(row.role) : 'tenant_owner';
    if (!out[role]) out[role] = [];
    out[role].push(row);
  }
  return out;
}

/**
 * Boshqa foydalanuvchi sifatida kirish (akkaunt paroli bilan tasdiqlash).
 * @param {{ userId: number | string; password: string }} body
 */
export async function adminImpersonate(body) {
  // NOTE: This request MUST NOT trigger global 401 auto-logout.
  // Wrong password is a business-level 401 and should only show an error in UI.
  const token = getStoredToken();
  const url = buildAbsoluteUrl(endpoints.auth.adminImpersonate);
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${String(token).trim()}` } : {}),
      ...getDeviceHeaders(),
    },
    body: JSON.stringify({
      user_id: Number(body.userId),
      password: String(body.password ?? ''),
    }),
  });
  let data = null;
  try {
    const text = await res.text();
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  if (!res.ok) {
    const err = new Error(data?.message ?? res.statusText ?? 'Request failed');
    err.status = res.status;
    err.response = { status: res.status, data: data ?? {} };
    throw err;
  }
  return data?.data ?? data;
}

/**
 * Parol to'g'riligini tekshirib, SMS OTP yuboradi (2FA login).
 * Token qaytarilmaydi — keyingi qadam verifyLogin(phone, code).
 * @param {string} phone - masalan "+998901234567"
 * @param {string} password
 * @returns {Promise<{ verification_id: string, expires_in: number, verification_code?: string }>}
 */
export async function sendLoginOtp(phone, password) {
  const normalizedPhone = normalizeLoginPhone(phone);
  const pwd = password != null ? String(password).trim() : '';
  if (!normalizedPhone || pwd.length < 1) {
    const err = new Error('missing_or_invalid_fields');
    err.code = 'missing_or_invalid_fields';
    throw err;
  }
  const url = buildAbsoluteUrl(endpoints.auth.sendLoginOtp);
  try {
    const res = await axios.post(
      url,
      { phone: normalizedPhone, password: pwd },
      {
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...getDeviceHeaders() },
        timeout: 20000,
      },
    );
    const root = res?.data ?? {};
    const data = root?.data && typeof root.data === 'object' ? root.data : root;
    return {
      verificationId: data?.verification_id ?? root?.verification_id ?? null,
      expiresIn: data?.expires_in ?? root?.expires_in ?? 120,
      verificationCode: data?.verification_code ?? root?.verification_code ?? null,
    };
  } catch (e) {
    const status = e?.response?.status;
    const data = e?.response?.data;
    const meta = data?.meta && typeof data.meta === 'object' ? data.meta : {};
    const authReason = meta.auth_reason ?? meta.authReason ?? data?.code ?? null;
    if (authReason) {
      const err = new Error(String(authReason));
      err.code = authReason;
      err.response = e.response;
      throw err;
    }
    throw e;
  }
}

/**
 * Parol tiklash uchun SMS OTP yuboradi.
 * purpose='reset_password' bilan POST /api/auth/send-sms.
 * @param {string} phone
 */
export async function sendResetPasswordOtp(phone) {
  const normalizedPhone = normalizeLoginPhone(phone);
  if (!normalizedPhone) {
    const err = new Error('phone_invalid');
    err.code = 'phone_invalid';
    throw err;
  }
  const url = buildAbsoluteUrl(endpoints.auth.sendSms);
  const res = await axios.post(
    url,
    { phone: normalizedPhone, purpose: 'reset_password' },
    {
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      timeout: 15000,
    },
  );
  const root = res?.data ?? {};
  const data = root?.data && typeof root.data === 'object' ? root.data : root;
  return {
    verificationId: data?.verification_id ?? root?.verification_id ?? null,
    expiresIn: data?.expires_in ?? root?.expires_in ?? 120,
    verificationCode: data?.verification_code ?? root?.verification_code ?? null,
  };
}

/**
 * OTP kodni tekshirib parolni yangilaydi.
 * POST /api/auth/reset-password
 * @param {{ phone: string, code: string, newPassword: string, newPasswordConfirm: string }} params
 */
export async function confirmResetPassword({ phone, code, newPassword, newPasswordConfirm }) {
  const normalizedPhone = normalizeLoginPhone(phone);
  if (!normalizedPhone) {
    const err = new Error('phone_invalid');
    err.code = 'phone_invalid';
    throw err;
  }
  const url = buildAbsoluteUrl(endpoints.auth.resetPassword);
  const res = await axios.post(
    url,
    {
      phone: normalizedPhone,
      code: String(code).trim(),
      new_password: String(newPassword).trim(),
      new_password_confirm: String(newPasswordConfirm).trim(),
    },
    {
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      timeout: 20000,
    },
  );
  return res?.data ?? {};
}

/**
 * Chiqish (serverda token invalidate qilish)
 */
export async function logoutApi() {
  try {
    await api.post(endpoints.auth.logout, {});
  } catch {
    // ignore
  }
}
